import { HeaderLabelChipStyle } from '@tableau/catalog-ui-temp';
import { SiteUrls } from '@tableau/shared-urls';
import { CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SiteUrlsProps } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { LabelWrapper } from '~src/catalog/CatalogLabelsHookHelper';
import { RefreshPropertyHelper } from '~src/extractRefresh/helpers/RefreshPropertyHelper';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ItemDescription } from '~src/spacesPlatform/components/header/HeaderItemDescription';
import { OwnerMetadata } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, DetailButtonIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { createContentButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { selectContentTotalLength } from '~src/spacesPlatform/selectors/ContentSelectors';

import { Messages } from '../../codegen/Localize';
import { ConnectedExtractMetadata } from '../../extractTask/components/spaces/header/ExtractMetadata';
import { ConnectedExtractTasksMetadata } from '../../extractTask/components/spaces/header/ExtractTasksMetadata';
import { FlowLinksWithIcon } from '../../flow/components/FlowLinksWithIcon';
import { FlowListItemInfo } from '../../flow/models/FlowInfo';
import { ISpaceItemDetailInfo } from '../../spacesPlatform/models/ISpaceItemDetailInfo';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { createEditDataSourceButton } from './DatasourceSpaceHeaderButtons';


const DATASOURCE_CREATE_CONTENT_FLYOUT_WIDTH: number = 260;

export interface DatasourceSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'datasource'>;
  readonly availableActions: IDatasourceActions;
  readonly flows: ReadonlyArray<FlowListItemInfo>;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly editDatasource: () => void;
  readonly catalogingOnSiteEnabled: boolean;
  readonly editingFlowsEnabled: boolean;
}

export function DatasourceSpaceHeader(props: DatasourceSpaceHeaderProps): JSX.Element {
  const existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());
  const showLegacySchedulesRetirementWarning = existingRefreshSchedulesCount > 0 && RefreshPropertyHelper.isDataSourceWithLegacyBridgeSchedule(props.item);
  return <>
    <ConnectedSpaceHeader
      item={props.item}
      availableActions={props.availableActions}
      detailIcons={
        <React.Fragment>
          {FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {DetailButtonIcon(props.item, props.openDetail)}
          {ActionsMenuIcon(props.item)}
        </React.Fragment>
      }
      metadataElement={DatasourceMetadataRow(props.item, props.flows, props.urls, props.availableActions)}
      descriptionElement={<ItemDescription item={props.item} />}
      bottomElement={
        <>
          <div className={`${ExplorerStyles.ToolbarButtonGroup} ${SpaceHeaderStyles.SpotlightActionButtonsRow}`} style={{ fontWeight: 'normal', }}>
            {createContentButton({
              // TFSID 1231210 will handle proper handling of create button, this is just a place holder for getting the details page to load
              projectId: props.item.project ? props.item.project.id : '',
              spaceType: props.item.contentType,
              flyoutWidth: DATASOURCE_CREATE_CONTENT_FLYOUT_WIDTH
            })}
            {createEditDataSourceButton({
              item: props.item,
              actions: props.availableActions,
              editDatasource: props.editDatasource,
              hasAssociatedFlows: props.flows.length > 0,
            })}
          </div>
          {FeatureFlags.bridgeLegacySchedulesRetirementMessageEnabled && showLegacySchedulesRetirementWarning ?
            <CompactInlineAlertWidget
              children={Messages.bridgeLegacySchedulesRetirementMessage()}
              type={InlineAlertType.Info}
              testId={DatasourceSpaceHeaderTestIds.legacySchedulesRetirementMsg}
            />
            : null}
        </>
      }
    />
  </>;
}

export function DatasourceMetadataRow(
  datasourceInfo: ISpaceItemDetailInfo<'datasource'>,
  flows: ReadonlyArray<FlowListItemInfo>,
  urls: SpacesSiteUrls,
  availableActions: IDatasourceActions,
): JSX.Element {

  return <React.Fragment>
    {OwnerMetadata(datasourceInfo.owner, urls)}
    <ConnectedExtractMetadata itemInfo={datasourceInfo} availableActions={availableActions} />
    <ConnectedExtractTasksMetadata itemInfo={datasourceInfo} availableActions={availableActions} />
    {PublishedByFlowMetadata(flows, urls)}
    <div style={{ marginLeft: '24px' }}>
      <LabelWrapper itemInfo={datasourceInfo} isEager={true} kind={'chip'}
        wrapper={(e) => <HeaderLabelChipStyle>{e}</HeaderLabelChipStyle>}
      />
    </div>
  </React.Fragment>;
}

function PublishedByFlowMetadata(flows: ReadonlyArray<FlowListItemInfo>, urls: SiteUrls): JSX.Element | null {
  if (flows.length > 0) {
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {Messages.datasourceHeaderPublishedByFlow()}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo}>
          <FlowLinksWithIcon flows={flows} hideIcon={true} siteUrls={urls} />
        </span>
      </>
    );
  } else {
    return null;
  }
}

export namespace DatasourceSpaceHeaderTestIds {
  export const legacySchedulesRetirementMsg = 'ds-space-header-legacy-schedules-retirement-alert';
}
